import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Check } from 'lucide-react';

interface MultiplePanelSelectorProps {
  panels: any[];
  onSubmit: (selectedPanels: any[]) => void;
  onCancel: () => void;
  loading: boolean;
  originalFileName: string;
}

const MultiplePanelSelector: React.FC<MultiplePanelSelectorProps> = ({
  panels,
  onSubmit,
  onCancel,
  loading,
  originalFileName
}) => {
  const [selectedPanels, setSelectedPanels] = useState<Set<number>>(new Set(Array.from({length: panels.length}, (_, i) => i)));
  const [panelChoices, setPanelChoices] = useState<{ [key: number]: 'new' | number }>({});

  const togglePanel = (index: number) => {
    const newSelected = new Set(selectedPanels);
    if (newSelected.has(index)) {
      newSelected.delete(index);
      // Remove choice when deselecting panel
      const newChoices = { ...panelChoices };
      delete newChoices[index];
      setPanelChoices(newChoices);
    } else {
      newSelected.add(index);
      // Default to submitting new panel when selecting
      setPanelChoices({ ...panelChoices, [index]: 'new' });
    }
    setSelectedPanels(newSelected);
  };

  const setPanelChoice = (panelIndex: number, choice: 'new' | number) => {
    setPanelChoices({ ...panelChoices, [panelIndex]: choice });
  };

  const handleSubmit = () => {
    const selected = panels.filter((_, index) => selectedPanels.has(index));
    const finalSubmission = selected.map((panel) => {
      const originalIndex = panels.indexOf(panel);
      const choice = panelChoices[originalIndex];
      
      // If user chose to use existing panel instead, skip this panel
      if (choice !== 'new' && typeof choice === 'number') {
        return null;
      }
      
      return panel;
    }).filter(Boolean);
    
    onSubmit(finalSubmission);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Multiple Panels Found</h3>
              <p className="text-gray-600">
                Found {panels.length} panels in {originalFileName}. Review duplicates and select panels to submit:
              </p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {panels.map((panel, index) => (
              <div key={index} className="border rounded-lg overflow-hidden">
                {/* Panel Header */}
                <div className="bg-gray-50 p-4 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedPanels.has(index)}
                        onChange={() => togglePanel(index)}
                        className="h-4 w-4 text-blue-600 mr-3"
                      />
                      <h4 className="font-semibold text-gray-900">
                        Panel {index + 1}: {panel.brand || 'Unknown'} {panel.model || ''}
                      </h4>
                      {panel.hasDuplicates && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          <Copy className="w-3 h-3 mr-1" />
                          {panel.duplicateInfo?.length} Duplicate{panel.duplicateInfo?.length !== 1 ? 's' : ''} Found
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Panel Content */}
                {selectedPanels.has(index) && (
                  <div className="p-4">
                    {panel.hasDuplicates ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* New Panel */}
                        <div
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                            panelChoices[index] === 'new' || !panelChoices[index]
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300'
                          }`}
                          onClick={() => setPanelChoice(index, 'new')}
                        >
                          <div className="flex items-center mb-3">
                            <input
                              type="radio"
                              name={`panel-${index}`}
                              checked={panelChoices[index] === 'new' || !panelChoices[index]}
                              onChange={() => setPanelChoice(index, 'new')}
                              className="h-4 w-4 text-green-600 mr-2"
                            />
                            <h5 className="font-medium text-green-900">Submit New Panel Request</h5>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div><span className="font-medium">Power:</span> {panel.wattage || '?'}W</div>
                            <div><span className="font-medium">Efficiency:</span> {panel.efficiency || '?'}%</div>
                            <div><span className="font-medium">Technology:</span> {panel.technology || '?'}</div>
                            <div><span className="font-medium">Dimensions:</span> {panel.length}×{panel.width}×{panel.thickness}mm</div>
                          </div>
                          {panel.description && (
                            <p className="text-sm text-gray-600 mt-2">{panel.description}</p>
                          )}
                        </div>

                        {/* Existing Panels */}
                        <div className="space-y-2">
                          <h5 className="font-medium text-gray-900 mb-2">Or Use Existing Panel:</h5>
                          <div className="max-h-48 overflow-y-auto space-y-2">
                            {panel.duplicateInfo?.map((duplicate: any, dupIndex: number) => (
                              <div
                                key={dupIndex}
                                className={`border-2 rounded-lg p-3 cursor-pointer transition-colors ${
                                  panelChoices[index] === dupIndex
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                }`}
                                onClick={() => setPanelChoice(index, dupIndex)}
                              >
                                <div className="flex items-center mb-2">
                                  <input
                                    type="radio"
                                    name={`panel-${index}`}
                                    checked={panelChoices[index] === dupIndex}
                                    onChange={() => setPanelChoice(index, dupIndex)}
                                    className="h-4 w-4 text-blue-600 mr-2"
                                  />
                                  <span className="font-medium text-sm text-gray-900">{duplicate.Description}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                                  <div>{duplicate.Watts_W}W</div>
                                  <div>{duplicate.ModuleEfficiency_percent}%</div>
                                  <div>{duplicate.CellType}</div>
                                  <div>SKU: {duplicate.SKU}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center mb-2">
                          <Check className="w-5 h-5 text-green-600 mr-2" />
                          <h5 className="font-medium text-green-900">New Panel (No Duplicates Found)</h5>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="font-medium">Power:</span> {panel.wattage || '?'}W</div>
                          <div><span className="font-medium">Efficiency:</span> {panel.efficiency || '?'}%</div>
                          <div><span className="font-medium">Technology:</span> {panel.technology || '?'}</div>
                          <div><span className="font-medium">Dimensions:</span> {panel.length}×{panel.width}×{panel.thickness}mm</div>
                        </div>
                        {panel.description && (
                          <p className="text-sm text-gray-600 mt-2">{panel.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              {selectedPanels.size} of {panels.length} panels selected
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || selectedPanels.size === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  `Submit ${selectedPanels.size} Request${selectedPanels.size !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MultiplePanelSelector;